import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Avatar,
  alpha,
  useTheme,
  Chip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ModernCard from './shared/ModernCard';

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && children}
  </div>
);

const MobileFriends = ({
  friends,
  pendingRequests,
  incomingRequests,
  onAcceptRequest,
  onRejectRequest,
  onInviteFriend,
  onRemoveFriend,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);

  const FriendsHeader = () => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Friends
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.main, 0.9) }}>
          Connect with other bonus hunters
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onInviteFriend}
          fullWidth
          sx={{
            mt: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 1.5,
            textTransform: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            },
          }}
        >
          Invite Friend
        </Button>
      </Stack>
    </Box>
  );

  const TabsSection = () => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 2,
        mb: 2,
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{
          minHeight: 48,
          '& .MuiTab-root': {
            color: alpha(theme.palette.primary.main, 0.7),
            textTransform: 'none',
            minHeight: 48,
            fontSize: '0.9rem',
            fontWeight: 500,
          },
          '& .Mui-selected': {
            color: theme.palette.primary.main,
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
          },
        }}
      >
        <Tab label={`Friends (${friends.length})`} />
        <Tab label={`Pending (${pendingRequests.length})`} />
        <Tab label={`Incoming (${incomingRequests.length})`} />
      </Tabs>
    </Box>
  );

  const FriendsList = () => (
    <Stack spacing={1}>
      {friends.length === 0 ? (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          No friends added yet
        </Typography>
      ) : (
        friends.map((friend) => (
          <ModernCard
            key={friend.id}
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                }}
              >
                {friend.displayName?.[0] || friend.email?.[0]}
              </Avatar>
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                  {friend.displayName || friend.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {friend.joinDate ? `Joined ${new Date(friend.joinDate).toLocaleDateString()}` : 'Recently joined'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`$${(friend.totalEarned || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.08),
                    color: theme.palette.success.main,
                    '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => onRemoveFriend && onRemoveFriend(friend.id)}
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    color: theme.palette.error.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </ModernCard>
        ))
      )}
    </Stack>
  );

  const PendingList = () => (
    <Stack spacing={1}>
      {pendingRequests.length === 0 ? (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          No pending requests
        </Typography>
      ) : (
        pendingRequests.map((request) => (
          <ModernCard
            key={request.id}
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  color: theme.palette.warning.main,
                }}
              >
                {request.toName?.[0] || request.toEmail?.[0]}
              </Avatar>
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                  {request.toName || request.toEmail}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Sent {new Date(request.createdAt).toLocaleDateString()}
                </Typography>
              </Stack>
              <Chip
                label="Pending"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  color: theme.palette.warning.main,
                  '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                }}
              />
            </Stack>
          </ModernCard>
        ))
      )}
    </Stack>
  );

  const IncomingList = () => (
    <Stack spacing={1}>
      {incomingRequests.length === 0 ? (
        <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
          No incoming requests
        </Typography>
      ) : (
        incomingRequests.map((request) => (
          <ModernCard
            key={request.id}
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  color: theme.palette.info.main,
                }}
              >
                {request.fromName?.[0] || request.fromEmail?.[0]}
              </Avatar>
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                  {request.fromName || request.fromEmail}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Received {new Date(request.createdAt).toLocaleDateString()}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <IconButton
                  size="small"
                  onClick={() => onAcceptRequest(request.id)}
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    color: theme.palette.success.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                    },
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onRejectRequest(request.id)}
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    color: theme.palette.error.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </ModernCard>
        ))
      )}
    </Stack>
  );

  return (
    <Stack spacing={0} sx={{ width: '100%', pb: 2, mt: '56px' }}>
      <FriendsHeader />
      <TabsSection />
      
      <TabPanel value={activeTab} index={0}>
        <FriendsList />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <PendingList />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <IncomingList />
      </TabPanel>
    </Stack>
  );
};

export default MobileFriends; 